<template>
  <div class="h-screen overflow-hidden flex flex-col">
    <div class="lg:grid lg:grid-cols-3 gap-[25px] flex-grow h-full overflow-hidden chat-container">
      <div class="lg:col-span-1 h-full overflow-hidden chat-sidebar" :class="{ 'hidden': selectedChat && isMobile }">
        <Sidebar @chat-selected="selectChat" @chats-loaded="handleChatsLoaded" />
      </div>
      <div class="lg:col-span-2 h-full overflow-hidden chat-panel" :class="{ 'hidden': !selectedChat && isMobile }">
        <ChatPanel 
          :selected-chat="selectedChat" 
          @back-to-list="backToList" 
          @update-bot-status="handleBotStatusUpdate" 
        />
      </div>
    </div>
  </div>
  <AddEventModal />
  <EditEventModal ref="editEventModalRef" />
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import Sidebar from "@/components/Apps/Chat/Sidebar.vue";
import ChatPanel from "@/components/Apps/Chat/index.vue";
import AddEventModal from "@/components/Apps/Calendar/AddEventModal.vue";
import EditEventModal from "@/components/Apps/Calendar/EditEventModal.vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// i18n setup
const { locale } = useI18n();

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Direct translation function that bypasses vue-i18n
const directT = (key: string): string => {
  const parts = key.split('.');
  let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
  
  for (const part of parts) {
    if (!result || !(part in result)) {
      console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
      // Fallback to English if translation not found
      let enResult: TranslationObject = translationFiles.en;
      for (const fallbackPart of parts) {
        if (!enResult || !(fallbackPart in enResult)) return key;
        enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
      }
      return typeof enResult === 'string' ? enResult : key;
    }
    result = result[part as keyof typeof result] as TranslationObject;
  }
  return typeof result === 'string' ? result : key;
};

// הגדרת ממשק לצ'אט
interface ChatData {
  id: string | number;
  name: string;
  image: string;
  lastMessage?: string;
  lastTime?: string;
  unread?: number;
  [key: string]: any; // לשדות נוספים שיכולים להיות
}

const editEventModalRef = ref<any>(null);
const selectedChat = ref<ChatData | null>(null);
const isMobile = ref(false);

// בדיקה אם המכשיר הוא מובייל
const checkIfMobile = (): void => {
  isMobile.value = window.innerWidth < 1024; // lg breakpoint in Tailwind
};

// בחירת צ'אט ומעבר לתצוגת צ'אט במובייל
const selectChat = (chat: ChatData): void => {
  selectedChat.value = chat;
};

// חזרה לרשימת הצ'אטים
const backToList = (): void => {
  if (isMobile.value) {
    selectedChat.value = null;
  }
};

// עקיבה אחר שינויי גודל המסך
onMounted(() => {
  checkIfMobile();
  window.addEventListener('resize', checkIfMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkIfMobile);
});

function openEditEventModal(event: any): void {
  if (editEventModalRef.value) {
    editEventModalRef.value.openModal(event);
  }
}

// Handle the update-bot-status event
function handleBotStatusUpdate(data: { threadId: string | number, botstatus: boolean }): void {
  console.log('Updating bot status in parent:', data);
  if (selectedChat.value && selectedChat.value.id === data.threadId) {
    // Create a new object to avoid mutating the original
    const updatedChat = Object.assign({}, selectedChat.value);
    updatedChat.botstatus = data.botstatus;
    selectedChat.value = updatedChat;
    console.log('Bot status updated in parent component:', selectedChat.value.botstatus);
  }
}

// Handle the chats-loaded event - auto-select first chat on desktop
function handleChatsLoaded(chats: ChatData[]): void {
  console.log('Chats loaded in parent component', { count: chats.length });
  
  // Auto-select the first chat only on desktop (not mobile)
  if (!isMobile.value && chats.length > 0 && !selectedChat.value) {
    console.log('Auto-selecting first chat on desktop');
    selectChat(chats[0]);
  }
}
</script>

<style scoped>
/* משתמשים ב-CSS global variables בעמוד הראשי */
:deep(.trezo-card) {
  margin-bottom: 0;
  height: 100%;
  box-shadow: 0 15px 60px rgba(0,0,0,0.25) !important;
  border: 1px solid rgba(0,0,0,0.05);
  filter: drop-shadow(0 10px 30px rgba(0,0,0,0.1));
}

.lg\:grid {
  gap: 30px !important;
}

.chat-sidebar :deep(.trezo-card),
.chat-panel :deep(.trezo-card) {
  box-shadow: 0 20px 80px rgba(0,0,0,0.3) !important;
}
</style>
